import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardMedia, Input, TextField, Button, MenuItem, Select } from "@mui/material";
import { useState, ChangeEvent } from "react";
import CloseIcon from '@mui/icons-material/Close';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { useAddSubAdminMutation, useGetPermissionsQuery, useUploadMediaMutation, useGetSubAdminByIdQuery, useUpdateSubAdminMutation } from "../../service/features/api";
import { toast } from "react-toastify";

const AddSubadmin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const id = location.state?.id;
    const isEdit = !!id;

    const [addSubAdmin, { isLoading: isAdding }] = useAddSubAdminMutation();
    const [updateSubAdmin, { isLoading: isUpdatingApi }] = useUpdateSubAdminMutation();
    const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();
    const { data: subAdminData, isLoading: isDataLoading } = useGetSubAdminByIdQuery(id, { skip: !isEdit });
    const { data: permissionsData } = useGetPermissionsQuery({ page: 1, limit: 100 });

    const [image, setImage] = useState<File | string>("");
    const [displayImage, setDisplayImage] = useState<string>("");

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        countryCode: "+91",
        permission: "default",
    });

    useEffect(() => {
        if (isEdit && subAdminData?.data) {
            const data = subAdminData.data;
            setFormData({
                fullName: data.fullName || "",
                email: data.email || "",
                phone: data.phone || "",
                countryCode: data.countryCode || "+91",
                permission: data.permission?._id || data.permission || "default",
            });
            if (data.profile) {
                setDisplayImage(data.profile);
            }
        }
    }, [subAdminData, isEdit]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!formData.fullName.trim()) return toast.error("Name is required");
        if (formData.fullName.length > 40) return toast.error("Name should not be more than 40 characters");
        if (!formData.email.trim()) return toast.error("Email is required");
        if (!formData.phone.trim()) return toast.error("Phone number is required");
        if (formData.permission === "default") return toast.error("Please select a role");

        let profileUrl = "";

        if (image instanceof File) {
            const mediaData = new FormData();
            mediaData.append("file", image);
            try {
                const res = await uploadMedia(mediaData).unwrap();
                profileUrl = res?.data || res?.url || res?.data?.data;
            } catch (error) {
                return toast.error("Failed to upload image");
            }
        }

        const payload = {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            countryCode: formData.countryCode,
            permission: formData.permission,
            profile: profileUrl || displayImage,
        };

        try {
            if (isEdit) {
                const response = await updateSubAdmin({ id, data: payload }).unwrap();
                toast.success(response.message || "Sub admin updated successfully", {
                    autoClose: 2000,
                });
            } else {
                const response = await addSubAdmin(payload).unwrap();
                toast.success(response.message || "Sub admin created successfully", {
                    autoClose: 2000,
                });
            }
            setTimeout(() => {
                navigate("/manage-subadmin");
            }, 2000);
        } catch (error: any) {
            toast.error(error?.data?.message || `Failed to ${isEdit ? 'update' : 'add'} sub-admin`);
        }
    };

    if (isEdit && isDataLoading) return <div className="loading_state">Loading...</div>;

    return (
        <>
            <CommonBreadcrumb
                title={isEdit ? "Edit Sub-Admin" : "Add Sub-Admin"}
                breadcrumbItems={[
                    { label: "Dashboard", onClick: () => navigate("/dashboard") },
                    { label: "Manage Sub-Admin", onClick: () => navigate("/manage-subadmin") },
                    { label: isEdit ? "Edit Sub-Admin" : "Add Sub-Admin" },
                ]}
            />

            <div className="cards">
                <div className="form">
                    <div className="gap_p">
                        <div className="control_group w_100">
                            <label htmlFor="icon-button-file">Upload Image</label>
                            {displayImage ? (
                                <div className="upload_image">
                                    <div className="upload_image_holder">
                                        <figure>
                                            <CardMedia component="img" image={displayImage} alt="photo" />
                                        </figure>
                                        <CloseIcon
                                            onClick={() => {
                                                setImage("");
                                                setDisplayImage("");
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <label className="upload_image" htmlFor="icon-button-file">
                                    <Input
                                        sx={{ display: "none" }}
                                        id="icon-button-file"
                                        type="file"
                                        inputProps={{
                                            accept: "image/png,image/jpeg",
                                        }}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            const files = event.target.files;
                                            if (files && files[0].type.includes("image")) {
                                                setImage(files[0]);
                                                setDisplayImage(URL.createObjectURL(files[0]));
                                            }
                                        }}
                                    />
                                    <span className="upload_image_holder">
                                        <figure className="add_img">
                                            <img src="/static/images/user_placeholder.png" alt="icon" />
                                            <span className="upload_img_icon">
                                                <img src="/static/images/edit2_icon.svg" alt="icon" />
                                            </span>
                                        </figure>
                                    </span>
                                </label>
                            )}
                        </div>

                        <div className="control_group w_33">
                            <label htmlFor="name">Name</label>
                            <TextField
                                id="name"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                placeholder="Enter Name"
                                hiddenLabel
                                fullWidth
                                inputProps={{ maxLength: 40 }}
                            />
                        </div>

                        <div className="control_group w_33">
                            <label htmlFor="email">Email Id</label>
                            <TextField
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter Email Id"
                                hiddenLabel
                                fullWidth
                            />
                        </div>

                        <div className="control_group w_33">
                            <label htmlFor="phone">Phone Number</label>
                            <PhoneInput
                                country={"in"}
                                value={formData.countryCode + formData.phone}
                                onChange={(phone, country: any) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        phone: phone.slice(country.dialCode.length),
                                        countryCode: `+${country.dialCode}`,
                                    }));
                                }}
                                placeholder="Enter Phone Number"
                                enableSearch={false}
                                inputStyle={{ width: "100%" }}
                            />
                        </div>

                        <div className="control_group w_33">
                            <label htmlFor="role">Role</label>
                            <Select
                                id="role"
                                fullWidth
                                name="permission"
                                value={formData.permission}
                                onChange={(e: any) => setFormData(prev => ({ ...prev, permission: e.target.value }))}
                            >
                                <MenuItem disabled value="default">
                                    Select Role
                                </MenuItem>
                                {permissionsData?.data?.data?.map((role: any) => (
                                    <MenuItem key={role._id} value={role._id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="form_btn">
                        <Button
                            className="btn btn_primary"
                            onClick={handleSave}
                            disabled={isAdding || isUpdatingApi || isUploading}
                        >
                            {isAdding || isUpdatingApi || isUploading ? (isEdit ? "Updating..." : "Saving...") : (isEdit ? "Update" : "Save")}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddSubadmin;
